import React from 'react';
import T from 'prop-types';
import { keys, isNumber, isEmpty, isNil } from 'lodash-es';
import { number_with_delimiter as numberWithDelimiter } from 'number_helpers';

import Tooltip from '../../../../../components/Tooltip';
import { decorateTooltipForecastLabel } from '../../config/forecasts';

const percentValues = ['forecast_yoy', 'reported_yoy', 'consensus_yoy', 'delta', 'change', 'forecast_error'];

const getCellWidth = (columnsLength, cellIndex) => cellIndex === 0
  ? 120
  : (518 / ((columnsLength - 2) || 1));

const ForecastsTable = ({
  activeForecast,
  calculateCellWidth = true,
}) => {
  const {
    rows,
  } = activeForecast?.forecasts || {};

  if (isEmpty(rows)) return null;

  const formatValue = (value, type) => {
    if (type === 'consensus' && value === 0) return 'N/A';

    if (type === 'consensus_yoy' && value === -100) return 'N/A';

    if (type === 'delta' && value === '0.0%') return 'N/A';

    const isPercentage = percentValues.includes(type);
    if (type === 'header' && value === undefined) return null;

    if (isNumber(value)) {
      if (isPercentage) {
        return `${value}%`;
      }

      return numberWithDelimiter(value);
    }

    if (value === undefined) return '—';

    return value;
  };

  const formatTooltipValue = (value, type, shortForecastLabel, column) => !isNil(value) && (
    <div className="series-tooltip">
      <span className="l-h-md d-inline-block"><b>{decorateTooltipForecastLabel(shortForecastLabel)}</b></span>
      <table className="table">
        <tbody>
          <tr>
            <td className="v-a-m text-left text-12 p-t-xxxs p-r-mdl p-b-0 p-l-0">
              <span className="l-h-md">{type === 'Apptopia Forecast' ? <b>Apptopia Forecast</b> : type}</span>
            </td>
            <td className="v-a-m text-right text-12 p-t-xxxs p-r-0 p-b-0 p-l-0">
              <span className="l-h-md">
                {value && (percentValues.includes(column)
                  ? (isNumber(value) ? `${value}%` : value)
                  : `${numberWithDelimiter(value)} ${activeForecast?.forecasts?.magnitude}`)}
              </span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <table className="table table-sm forecast-table">
      <tbody>
        {keys(rows).map((row, i) => (
          <tr key={row}>
            {rows.header.map((column, idx) => (
              <Tooltip
                key={column + idx}
                id={column}
                placement="topLeft"
                disabled={row === 'header' || idx === 0}
                colorVariant="light"
                overlayClassName="forecast-table-tooltip"
                content={formatTooltipValue(rows[row][idx], rows[row][0], column, row)}
              >
                <td
                  style={{
                    width: calculateCellWidth ? getCellWidth(rows.header.length, idx) : null,
                  }}
                  {...(i === 0 ? { 'data-key': column } : {})}
                >
                  <div className="text-truncate">{formatValue(rows[row][idx], row)}</div>
                </td>
              </Tooltip>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

ForecastsTable.propTypes = {
  activeForecast: T.oneOfType([
    T.object,
    T.string,
  ]),
  calculateCellWidth: T.bool,
};

export default ForecastsTable;
